import React, { useState, useEffect, useMemo } from 'react';
import {
  Badge,
  Box,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import { PencilSimple, Trash, Eye, Star } from 'phosphor-react';
import { useActionToast } from '../../services/actionToast';
import { PageTitle } from '../../components/PageTitle';
import { DataSkeleton, EmptyState, ErrorState } from '../../components/states';
import { useFetch } from '../../hooks/useFetch';
import { api } from '../../services/api';
import { CreateButton } from '../../components/buttons/CreateButton';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  ConfirmDialog,
  Dialog,
  FormDialog,
  FormRow,
  FramedCard,
  FramedCardContent,
  PageAction,
  SearchField,
  Toolbar,
  usePageActionSlot,
} from '../../components/ui';

interface MaterialModule {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  is_default: boolean;
  display_order: number;
  templates_count: number;
  institutions_count: number;
  created_at: string;
}

interface Template {
  id: number;
  name: string;
  type: string;
  category: string;
  school_class: string;
}

const emptyForm = {
  name: '',
  description: '',
  is_active: true,
  is_default: false,
  display_order: 0,
};

const apiMessage = (error: any, fallback: string) =>
  error?.response?.data?.msg || error?.response?.data?.message || fallback;

export function MaterialModulesPage() {
  const [selectedModule, setSelectedModule] = useState<MaterialModule | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [formData, setFormData] = useState(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<MaterialModule | null>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState(false);
  const [search, setSearch] = useState('');

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const toast = useActionToast();
  const { node: pageActionNode } = usePageActionSlot();

  const {
    requestApi: fetchModules,
    data: modulesData,
    loading: loadingModules,
    error: modulesError,
  } = useFetch('/v1/admin/material-modules', { actionFeedback: false });

  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    await fetchModules('get');
  };

  const handleViewModule = async (module: MaterialModule) => {
    setSelectedModule(module);
    setLoadingTemplates(true);
    setTemplatesError(false);
    setTemplates([]);
    onViewOpen();

    try {
      const response = await api.get(`/v1/admin/material-modules/${module.id}?with_templates=true`);
      setTemplates(response.data.templates || []);
    } catch {
      setTemplatesError(true);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleCreate = () => {
    setFormData(emptyForm);
    setFormError(null);
    setIsEditing(false);
    onOpen();
  };

  const handleEdit = (module: MaterialModule) => {
    setFormData({
      name: module.name,
      description: module.description,
      is_active: module.is_active,
      is_default: module.is_default,
      display_order: module.display_order,
    });
    setFormError(null);
    setSelectedModule(module);
    setIsEditing(true);
    onOpen();
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!formData.name.trim()) {
      setFormError('Informe o nome do módulo.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      if (isEditing && selectedModule) {
        await api.put(`/v1/admin/material-modules/${selectedModule.id}`, formData);
        toast({ title: 'Módulo atualizado', status: 'success', duration: 2000 });
      } else {
        await api.post('/v1/admin/material-modules', formData);
        toast({ title: 'Módulo criado', status: 'success', duration: 2000 });
      }
      onClose();
      loadModules();
    } catch (error: any) {
      setFormError(apiMessage(error, 'Não foi possível salvar o módulo.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/v1/admin/material-modules/${deleteTarget.id}`);
      toast({ title: 'Módulo excluído', status: 'success', duration: 2000 });
      setDeleteTarget(null);
      loadModules();
    } catch (error: any) {
      toast({
        title: 'Erro ao excluir módulo',
        description: apiMessage(error, 'Não foi possível excluir o módulo.'),
        status: 'error',
        duration: 4000,
      });
    } finally {
      setDeleting(false);
    }
  };

  const modules: MaterialModule[] = useMemo(() => {
    const list = Array.isArray(modulesData) ? modulesData : modulesData?.data;
    return Array.isArray(list) ? list : [];
  }, [modulesData]);
  const filteredModules = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return modules;
    return modules.filter(
      (m) =>
        m.name.toLowerCase().includes(term) || (m.description ?? '').toLowerCase().includes(term)
    );
  }, [modules, search]);

  const createButton = (
    <CreateButton onClick={handleCreate} data-testid="create-module-button">
      Novo módulo
    </CreateButton>
  );

  const renderTable = () => {
    if ((loadingModules || !modulesData) && !modulesError && modules.length === 0) {
      return <DataSkeleton variant="table" rows={5} columns={6} label="Carregando módulos" />;
    }
    if (modulesError && modules.length === 0) {
      return (
        <ErrorState
          description="Não foi possível carregar os módulos de materiais."
          onRetry={loadModules}
        />
      );
    }
    if (modules.length === 0) {
      return (
        <EmptyState
          variant="bare"
          title="Nenhum módulo configurado"
          description="Crie o primeiro módulo para organizar os materiais."
          action={{ label: 'Criar primeiro módulo', onClick: handleCreate }}
        />
      );
    }
    if (filteredModules.length === 0) {
      return (
        <EmptyState
          variant="bare"
          title="Nenhum módulo encontrado"
          description="Ajuste a busca para ver outros módulos."
          action={{ label: 'Limpar busca', onClick: () => setSearch('') }}
        />
      );
    }

    return (
      <Table variant="serra">
        <Thead>
          <Tr>
            <Th w="72px">Ordem</Th>
            <Th>Nome</Th>
            <Th>Descrição</Th>
            <Th isNumeric>Templates</Th>
            <Th isNumeric>Instituições</Th>
            <Th>Status</Th>
            <Th w="120px" />
          </Tr>
        </Thead>
        <Tbody>
          {filteredModules.map((module) => (
            <Tr key={module.id} data-testid={`module-row-${module.id}`}>
              <Td fontFamily="mono" fontSize="12px" color="content.subtle">
                {module.display_order}
              </Td>
              <Td>
                <Flex align="center" gap="8px">
                  <Text fontWeight={500}>{module.name}</Text>
                  {module.is_default && (
                    <Tooltip label="Módulo padrão">
                      <Box color="state.warning" display="flex">
                        <Star size={14} weight="fill" />
                      </Box>
                    </Tooltip>
                  )}
                </Flex>
              </Td>
              <Td>
                <Text color="content.body" noOfLines={1} maxW="420px">
                  {module.description || '-'}
                </Text>
              </Td>
              <Td isNumeric fontFamily="mono" fontSize="12px">
                {module.templates_count}
              </Td>
              <Td isNumeric fontFamily="mono" fontSize="12px">
                {module.institutions_count}
              </Td>
              <Td>
                <Badge variant={module.is_active ? 'success' : 'neutral'}>
                  {module.is_active ? 'Ativo' : 'Inativo'}
                </Badge>
              </Td>
              <Td pr="16px">
                <Flex justify="flex-end" gap="2px">
                  <IconButton
                    aria-label="Ver detalhes"
                    icon={<Eye size={16} />}
                    size="sm"
                    variant="ghost"
                    color="content.body"
                    onClick={() => handleViewModule(module)}
                    data-testid={`view-module-${module.id}`}
                  />
                  <IconButton
                    aria-label="Editar"
                    icon={<PencilSimple size={16} />}
                    size="sm"
                    variant="ghost"
                    color="content.body"
                    onClick={() => handleEdit(module)}
                    data-testid={`edit-module-${module.id}`}
                  />
                  <Tooltip
                    label={
                      module.institutions_count > 0
                        ? 'Desvincule as instituições antes de excluir'
                        : 'Excluir'
                    }
                  >
                    <IconButton
                      aria-label="Excluir"
                      icon={<Trash size={16} />}
                      size="sm"
                      variant="ghost"
                      color="content.body"
                      _hover={{ bg: 'state.destructiveSurface', color: 'state.destructive' }}
                      onClick={() => setDeleteTarget(module)}
                      isDisabled={module.institutions_count > 0}
                      data-testid={`delete-module-${module.id}`}
                    />
                  </Tooltip>
                </Flex>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  };

  return (
    <Box>
      <PageTitle description="Organize os materiais extras em módulos temáticos.">
        Módulos de Materiais Extras
      </PageTitle>
      {pageActionNode && <PageAction>{createButton}</PageAction>}

      <Alert variant="info" mb="16px">
        <AlertTitle>O que são Módulos de Materiais?</AlertTitle>
        <AlertDescription>
          Módulos agrupam materiais extras relacionados em pacotes reutilizáveis. Um módulo pode ser
          aplicado a vários templates e instituições de uma só vez, mantendo os conteúdos
          padronizados.
        </AlertDescription>
      </Alert>

      <FramedCard>
        <FramedCardContent p={0} gap={0}>
          <Toolbar justify="space-between">
            <Text fontSize="13px" color="content.body">
              {modules.length} {modules.length === 1 ? 'módulo' : 'módulos'}
            </Text>
            <Flex align="center" gap="8px">
              <SearchField
                value={search}
                onChange={setSearch}
                label="Buscar módulos"
                placeholder="Buscar por nome ou descrição"
              />
              {!pageActionNode && createButton}
            </Flex>
          </Toolbar>
          {renderTable()}
        </FramedCardContent>
      </FramedCard>

      <FormDialog
        isOpen={isOpen}
        onClose={onClose}
        title={isEditing ? 'Editar módulo' : 'Novo módulo'}
        description="Módulos agrupam templates e materiais para reutilização em várias instituições."
        onSubmit={handleSave}
        isLoading={saving}
        submitLabel={isEditing ? 'Salvar alterações' : 'Criar módulo'}
        submitTestId="save-module-button"
      >
        {formError && (
          <Alert variant="error">
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}
        <FormControl isRequired isInvalid={Boolean(formError) && !formData.name.trim()}>
          <FormLabel htmlFor="module-name">Nome</FormLabel>
          <Input
            id="module-name"
            placeholder="Ex.: Fundamental I - Completo"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            data-testid="module-name-input"
          />
        </FormControl>
        <FormControl>
          <FormLabel htmlFor="module-description">Descrição</FormLabel>
          <Textarea
            id="module-description"
            placeholder="Quando usar este módulo e o que ele contém"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={3}
            data-testid="module-description-input"
          />
        </FormControl>
        <FormRow gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }}>
          <FormControl>
            <FormLabel htmlFor="module-order">Ordem de exibição</FormLabel>
            <Input
              id="module-order"
              type="number"
              value={formData.display_order}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  display_order: Number.isNaN(parseInt(e.target.value, 10))
                    ? 0
                    : parseInt(e.target.value, 10),
                })
              }
              data-testid="module-order-input"
            />
          </FormControl>
          <FormControl>
            <FormLabel htmlFor="module-active">Status</FormLabel>
            <Flex as="label" htmlFor="module-active" align="center" gap="10px" h="38px">
              <Switch
                id="module-active"
                isChecked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                data-testid="module-active-switch"
              />
              <Text fontSize="13px" color="content.body">
                Ativo
              </Text>
            </Flex>
          </FormControl>
        </FormRow>
        <FormControl>
          <FormLabel htmlFor="module-default">Módulo padrão</FormLabel>
          <Flex as="label" htmlFor="module-default" align="center" gap="10px" h="38px">
            <Switch
              id="module-default"
              isChecked={formData.is_default}
              onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
              data-testid="module-default-switch"
            />
            <Text fontSize="13px" color="content.body">
              Aplicar automaticamente em novas instituições
            </Text>
          </Flex>
        </FormControl>
      </FormDialog>

      <Dialog
        isOpen={isViewOpen}
        onClose={onViewClose}
        size="xl"
        title={selectedModule?.name ?? 'Módulo'}
        description={selectedModule?.description || undefined}
      >
        {loadingTemplates && (
          <DataSkeleton variant="detail" rows={5} label="Carregando detalhes do módulo" />
        )}
        {!loadingTemplates && templatesError && (
          <ErrorState
            onRetry={() => selectedModule && handleViewModule(selectedModule)}
            description="Não foi possível carregar os templates do módulo."
          />
        )}
        {!loadingTemplates && !templatesError && (
          <Box>
            <Flex align="center" justify="space-between" mb="8px">
              <Text fontSize="13px" fontWeight={500} color="content.strong">
                Templates
              </Text>
              <Badge variant="neutral">{templates.length}</Badge>
            </Flex>
            {templates.length === 0 ? (
              <EmptyState
                variant="bare"
                title="Nenhum template vinculado"
                description="Este módulo ainda não contém templates."
              />
            ) : (
              <Box
                maxH="400px"
                overflowY="auto"
                border="1px solid"
                borderColor="line.hairline"
                borderRadius="lg"
              >
                <Table variant="serra" size="sm">
                  <Thead position="sticky" top={0} bg="surface.base" zIndex={1}>
                    <Tr>
                      <Th>Nome</Th>
                      <Th>Tipo</Th>
                      <Th>Categoria</Th>
                      <Th>Turma</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {templates.map((template) => (
                      <Tr key={template.id}>
                        <Td>{template.name}</Td>
                        <Td>
                          <Badge variant="neutral">{template.type}</Badge>
                        </Td>
                        <Td color="content.body">{template.category || '-'}</Td>
                        <Td color="content.body">{template.school_class || '-'}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>
        )}
      </Dialog>

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Excluir módulo"
        description={`O módulo "${deleteTarget?.name}" será removido. Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        isDestructive
        isLoading={deleting}
      />
    </Box>
  );
}

export default MaterialModulesPage;
